import React, { useEffect, useMemo, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow
} from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import { formatInteger, parseNumberPtBr } from '../../utils/numberUtils';
import '../css/crmDashboard.css';
import '../css/crmInfluencerDashboard.css';
import {
    buildInfluencerDashboardViewModel,
    buildParceiroLink,
    fetchCrmInfluencerLeads,
    INFLUENCER_TABLE_PAGE,
    INFLUENCER_TABLE_PAGE_SIZE,
    INFLUENCER_TABLE_PAGE_SIZE_OPTIONS
} from './crmInfluencerDashboardData';

const formatKwh = (value) => `${formatInteger(Math.round(value))} kWh`;

const metricConfig = [
    { key: 'totalLeads', label: 'Leads cadastrados', icon: GroupsRoundedIcon, tone: 'blue', formatter: formatInteger },
    { key: 'contractedLeads', label: 'Clientes contratados', icon: VerifiedRoundedIcon, tone: 'green', formatter: formatInteger },
    { key: 'conversionRate', label: 'Taxa de conversão', icon: PercentRoundedIcon, tone: 'amber', formatter: (value) => `${value}%` },
    { key: 'soldKwh', label: 'kWh vendidos', icon: BoltRoundedIcon, tone: 'navy', formatter: formatKwh }
];

const DashboardMetric = ({ icon: Icon, label, value, tone, formatter }) => (
    <div className={`crm-influencer-kpi is-${tone}`}>
        <div className='crm-influencer-kpi-icon'>
            <Icon fontSize='small' />
        </div>
        <span>{label}</span>
        <strong>{formatter(value)}</strong>
    </div>
);

const PerformanceChart = ({ rows }) => {
    const maxValue = Math.max(...rows.map((row) => row.leads), 1);

    return (
        <Paper className='crm-influencer-panel crm-influencer-chart-panel'>
            <div className='crm-influencer-panel-heading'>
                <div>
                    <p className='crm-influencer-eyebrow'>Performance</p>
                    <h2>Avanço por fase</h2>
                </div>
                <TrendingUpRoundedIcon />
            </div>

            <div className='crm-influencer-bars'>
                {rows.map((row) => (
                    <div key={row.label} className='crm-influencer-bar-row'>
                        <div className='crm-influencer-bar-label'>
                            <span>{row.label}</span>
                            <strong>{formatInteger(row.leads)}</strong>
                        </div>
                        <div className='crm-influencer-bar-track'>
                            <span style={{ width: `${(row.leads / maxValue) * 100}%` }} />
                        </div>
                    </div>
                ))}
            </div>
        </Paper>
    );
};

const LinkCouponPanel = ({ coupon, link, onCopy }) => (
    <Paper className='crm-influencer-panel crm-influencer-link-panel'>
        <div className='crm-influencer-panel-heading'>
            <div>
                <p className='crm-influencer-eyebrow'>Ativação</p>
                <h2>Link e cupom</h2>
            </div>
            <BoltRoundedIcon />
        </div>

        <div className='crm-influencer-share-list'>
            <div>
                <span>Link da campanha</span>
                <button type='button' onClick={() => onCopy(link, 'Link copiado.')}>
                    <strong>{link}</strong>
                    <ContentCopyRoundedIcon fontSize='small' />
                </button>
            </div>
            <div>
                <span>Cupom</span>
                <button type='button' onClick={() => onCopy(coupon, 'Cupom copiado.')}>
                    <strong>{coupon}</strong>
                    <ContentCopyRoundedIcon fontSize='small' />
                </button>
            </div>
        </div>
    </Paper>
);

const OpportunitiesTable = ({ rows }) => {
    const [page, setPage] = useState(INFLUENCER_TABLE_PAGE);
    const [pageSize, setPageSize] = useState(INFLUENCER_TABLE_PAGE_SIZE);

    useEffect(() => {
        setPage(INFLUENCER_TABLE_PAGE);
    }, [rows]);

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

    return (
        <Paper className='crm-influencer-panel crm-influencer-table-panel'>
            <div className='crm-influencer-panel-heading'>
                <div>
                    <p className='crm-influencer-eyebrow'>Oportunidades</p>
                    <h2>Leads da campanha</h2>
                </div>
            </div>

            <TableContainer className='crm-influencer-table-shell'>
                <Table size='small' stickyHeader className='crm-influencer-table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Lead</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Fase</TableCell>
                            <TableCell>Origem</TableCell>
                            <TableCell>Campanha</TableCell>
                            <TableCell align='right'>kWh</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((row, index) => (
                            <TableRow key={`${row.lead_id ?? ''}-${index}`} hover>
                                <TableCell>
                                    <div className='crm-influencer-lead-cell'>
                                        <strong>{row.nome ?? ''}</strong>
                                        <span>{row.email ?? ''}</span>
                                    </div>
                                </TableCell>
                                <TableCell>{row.status ?? ''}</TableCell>
                                <TableCell>{row.fase ?? ''}</TableCell>
                                <TableCell>{row.origem ?? ''}</TableCell>
                                <TableCell>{row.campaign ?? ''}</TableCell>
                                <TableCell align='right'>{formatKwh(parseNumberPtBr(row.ticket))}</TableCell>
                            </TableRow>
                        ))}
                        {pageRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align='center' className='crm-influencer-feedback-cell'>
                                    Nenhum lead encontrado.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <div className='crm-influencer-pagination-shell'>
                <span className='crm-influencer-pagination-count'>
                    Página {page} de {totalPages}
                </span>
                <TablePagination
                    component='div'
                    count={rows.length}
                    page={page - 1}
                    onPageChange={(event, newPage) => setPage(newPage + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(event) => {
                        setPageSize(parseInt(event.target.value, 10));
                        setPage(INFLUENCER_TABLE_PAGE);
                    }}
                    rowsPerPageOptions={INFLUENCER_TABLE_PAGE_SIZE_OPTIONS}
                    labelRowsPerPage='Leads por página'
                />
            </div>
        </Paper>
    );
};

const CrmInfluencerDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    useEffect(() => {
        let ignore = false;

        const loadInfluencerLeads = async () => {
            setIsLoading(true);

            try {
                const data = await fetchCrmInfluencerLeads();
                if (!ignore) setDashboardData(data);
            } catch (error) {
                if (!ignore) {
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        loadInfluencerLeads();
        return () => { ignore = true; };
    }, []);

    const handleCopy = async (value, feedback) => {
        await navigator.clipboard.writeText(value);
        setMessage(feedback);
        setStatusAlerta('success');
        setOpenAlerta(true);
    };

    const viewModel = useMemo(
        () => (dashboardData ? buildInfluencerDashboardViewModel(dashboardData) : null),
        [dashboardData]
    );
    const coupon = dashboardData?.coupon ?? '';
    const partnerName = dashboardData?.name ?? '';
    const campaignLink = dashboardData ? buildParceiroLink(coupon) : '';

    return (
        <Box className='mainBox crm-influencer-page'>
            <PageHeader>{partnerName}</PageHeader>

            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <div className='crm-influencer-shell'>
                {isLoading && <LinearProgress className='crm-influencer-loading' variant='indeterminate' />}

                {viewModel && (
                    <>
                        <section className='crm-influencer-kpi-grid'>
                            {metricConfig.map((metric) => (
                                <DashboardMetric
                                    key={metric.key}
                                    {...metric}
                                    value={viewModel[metric.key]}
                                />
                            ))}
                        </section>

                        <section className='crm-influencer-main-grid'>
                            <PerformanceChart rows={viewModel.stagePerformance} />
                            <LinkCouponPanel
                                coupon={coupon}
                                link={campaignLink}
                                onCopy={handleCopy}
                            />
                        </section>

                        <OpportunitiesTable rows={viewModel.opportunities} />
                    </>
                )}
            </div>
        </Box>
    );
};

export default CrmInfluencerDashboard;
